/**
 * Витрина свежего отчёта.
 *
 * В хранилище приходят за последним документом — остальное там лежит на
 * случай «а что было в марте». Поэтому свежий отчёт вынесен из списка и
 * показан целиком: период, балл с дельтой, деньги под риском, главная мысль.
 * Список ниже начинается уже с предыдущего месяца, чтобы верхняя запись не
 * дублировалась.
 *
 * Витрина кликабельна целиком — как и строки списка. Кнопки «Открыть отчёт»
 * внутри нет намеренно: она повторяла бы то же действие вторым элементом, а
 * читателю пришлось бы целиться в неё вместо карточки.
 *
 * Балл набран крупно и в цвет своей полосы — это единственное число, ради
 * которого сюда заходят, не открывая документ. Набирает его `Metric` из
 * отчёта: витрина и документ обязаны показывать одну и ту же цифру одним и
 * тем же приёмом, иначе переход между ними читается как смена продукта.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {Metric} from '../../ExecutiveReport/components/Metric';
import {ReportCard} from '../../ExecutiveReport/components/ReportCard';
import {useReportFormat} from '../../ExecutiveReport/format';
import {
  MEASURE,
  longDate,
  reportPeriodTitle,
  shortDate,
  signed,
} from '../../ExecutiveReport/helpers';
import {deltaTone, riskSharePct, scoreTone, statusMeta} from '../helpers';
import type {ReportEntry} from '../types';

// Подписи с неразрывными пробелами живут строками: в JSX-тексте NBSP
// запрещён линтером, а типографика нужна именно в отрендеренном тексте.
const SCORE_LABEL = 'Оценка кабинета';
const SCORE_MAX = 'из 100';
const DELTA_SUFFIX = ' к прошлому месяцу';
const RISK_LABEL = 'Деньги под риском';
const SHARE_PREFIX = ' % от расхода (';
const SHARE_SUFFIX = ')';

export interface LatestReportProps {
  entry: ReportEntry;
  onOpen?: (id: string) => void;
}

const Period = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

const Sub = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

const Headline = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

/**
 * Дельта балла — строкой под самим баллом, а не бейджем сбоку. Она читается
 * продолжением числа («34 из 100, минус семь к прошлому месяцу»), и пилюля
 * рядом превращала это продолжение в отдельный ярлык. Тон остаётся: подпись
 * `Metric` приглушена, а рост и падение обязаны отличаться цветом.
 */
const Delta = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: 'good' | 'alert' | 'bad' | 'neutral'}>(({theme, $tone}) => ({
  color: $tone === 'neutral' ? surfaceVar('onSurfaceMuted') : theme.palette[$tone].main,
}));

export function LatestReport({entry, onOpen}: LatestReportProps) {
  // Валюта у сумм одна на весь отчётный дом и живёт в `format.ts`. Голые
  // `money`/`moneyParts` без неё роняют витрину: `Intl.NumberFormat` со
  // `style: 'currency'` требует код валюты и бросает, если его нет.
  const fmt = useReportFormat();
  const status = statusMeta(entry.status);
  // Заголовок витрины — из границ периода: целый месяц называется по имени,
  // обрезанный остаётся диапазоном.
  const periodLabel = reportPeriodTitle(entry.periodFrom, entry.periodTo);
  const delta =
    entry.score != null && entry.scorePrev != null ? entry.score - entry.scorePrev : null;
  const risk = entry.atRisk != null ? fmt.moneyParts(entry.atRisk) : null;
  const sharePct = entry.atRisk != null ? riskSharePct(entry.atRisk, entry.spend) : null;
  const openable = onOpen != null && entry.status === 'ready';

  return (
    // Витрина — карточка отчётного дома: приглушённая заливка `surface1` и
    // паддинг `l`. Сплошной токен, стоявший здесь раньше, делал её светлее
    // любого блока внутри самого отчёта.
    <ReportCard
      padding='l'
      onClick={openable ? () => onOpen(entry.id) : undefined}
      ariaLabel={openable ? `Открыть отчёт за ${periodLabel}` : undefined}
    >
      <Stack gap='l'>
        <Stack gap='xs'>
          <Row gap='s' alignY='center'>
            <Period>{periodLabel}</Period>
            <ChipMUI
              mode='badge'
              color={status.tone}
              variant='soft'
              size='s'
              leftIcon={status.icon}
            >
              {status.label}
            </ChipMUI>
          </Row>
          <Sub>
            {entry.date != null && `${status.dateLabel} ${longDate(entry.date)} · `}
            период {shortDate(entry.periodFrom)} — {shortDate(entry.periodTo)}
          </Sub>
        </Stack>

        {/* Оба числа набраны одной ступенью и с одинаковыми подписями:
            это два показателя одного веса, и разный кегль читался бы как
            «второе — уточнение к первому». Общий кегль ещё и ставит их
            строки вровень — подписи, цифры и пояснения идут по одним
            горизонталям. */}
        <Row gap='xl' alignY='start' wrap='wrap'>
          {entry.score != null && (
            <Metric
              size='xl'
              prominent
              label={SCORE_LABEL}
              value={entry.score}
              unit={SCORE_MAX}
              tone={scoreTone(entry.score)}
              caption={
                delta != null && delta !== 0 ? (
                  <Delta as='span' $tone={deltaTone(delta)}>
                    {signed(delta) + DELTA_SUFFIX}
                  </Delta>
                ) : undefined
              }
            />
          )}

          {risk != null && (
            <Metric
              size='xl'
              prominent
              label={RISK_LABEL}
              value={risk.value}
              unit={risk.unit}
              caption={
                sharePct != null && entry.spend != null
                  ? sharePct + SHARE_PREFIX + fmt.money(entry.spend) + SHARE_SUFFIX
                  : undefined
              }
            />
          )}
        </Row>

        {entry.headline != null && <Headline>{entry.headline}</Headline>}
      </Stack>
    </ReportCard>
  );
}
